import { useQuery } from '@apollo/client';
import {
  Assessment,
  Clear,
  Download,
  FiberManualRecord,
  FiberManualRecordOutlined,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import type { DateRange } from '@mui/x-date-pickers-pro';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import type { DateRangePickerDayProps } from '@mui/x-date-pickers-pro/DateRangePickerDay';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import { useGenerateReport } from '~/api/playout-reports/generate';
import { RouterBreadcrumbs } from '~/components/RouterBreadcrumbs';
import { RouterTabs } from '~/components/RouterTabs';
import { InfoTooltip } from '~/components/Tooltip/InfoTooltip';
import { EmptyState } from '~/components/empty-state';
import { LoadingPane } from '~/components/loading-pane';
import { PageContainer } from '~/components/page-layout';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import { useLocation } from '~/hooks/location';
import ReportingIllustration from '~/images/illustrations/reporting.svg';
import { formatDateOnly } from '~/lib/datetime';
import { EventMarkerDay } from './components/EventMarkerDay';
import { MultiSelectPicker } from './components/MultiSelectPicker';
import { ReportDownloadDialog } from './components/download-dialog';
import {
  DEFAULT_PLAYOUT_FILTERS,
  EMPTY_REPORT,
  TabsLoadingPane,
  serializePlayoutFilterParams,
  toDayKeySet,
  toEventNamesByDay,
  usePlayoutFilterParams,
  useTabs,
} from './lib';
import { PlayoutReportDocument } from './queries/PlayoutReport.generated';
import {
  PlayoutReportEventTypeEventsDocument,
  PlayoutReportEventTypesDocument,
  type PlayoutReportEventTypes__EventType,
} from './queries/PlayoutReportEventTypes.generated';
import { PlayoutReportsProvider } from './tabs';

export const PlayoutReports = () => {
  const { t } = useTranslation();
  const { currentNetwork, currentUser } = useAppContext();
  const tabs = useTabs();

  const { filters, setFilters } = usePlayoutFilterParams();
  const {
    eventIds: selectedEventIds,
    deviceGroupIds: selectedDeviceGroupIds,
    combineByName,
  } = filters;
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  const { data: filterData, loading: filtersLoading } = useQuery(PlayoutReportEventTypesDocument, {
    variables: { networkId: currentNetwork.id },
  });

  const eventTypes = filterData?.network?.allEventTypes ?? [];
  const eventTypeId =
    filters.eventTypeId !== null &&
    filterData &&
    !eventTypes.some((et) => et.id === filters.eventTypeId)
      ? null
      : filters.eventTypeId;
  const selectedEventType = eventTypes.find((et) => et.id === eventTypeId) ?? null;

  const { data: eventsData, loading: eventsLoading } = useQuery(
    PlayoutReportEventTypeEventsDocument,
    {
      skip: eventTypeId === null,
      variables: { networkId: currentNetwork.id, eventTypeId: eventTypeId ?? 0 },
    },
  );

  const allEventTypeEvents = useMemo(
    () => eventsData?.network?.eventType.events ?? [],
    [eventsData],
  );

  const dateRange = useMemo<DateRange<DateTime>>(() => {
    if (filters.dateRange[0] || filters.dateRange[1]) return filters.dateRange;
    const firstEvent = allEventTypeEvents.at(0);
    const lastEvent = allEventTypeEvents.at(-1);
    if (!firstEvent || !lastEvent) return [null, null];
    return [
      DateTime.fromISO(firstEvent.start).startOf('day'),
      DateTime.fromISO(lastEvent.start).startOf('day'),
    ];
  }, [filters.dateRange, allEventTypeEvents]);

  const eventIdsWithRollups = useMemo(
    () => new Set(eventsData?.network?.eventType.eventIdsWithRollups ?? []),
    [eventsData],
  );

  const handleEventTypeChange = (eventType: PlayoutReportEventTypes__EventType | null) =>
    setFilters({
      eventTypeId: eventType?.id ?? null,
      dateRange: [null, null],
      eventIds: null,
    });

  const eventsInRange = useMemo(() => {
    if (!dateRange[0] || !dateRange[1]) return allEventTypeEvents;
    const from = dateRange[0].startOf('day');
    const to = dateRange[1].endOf('day');
    return allEventTypeEvents.filter((e) => {
      const start = DateTime.fromISO(e.start);
      return start >= from && start <= to;
    });
  }, [allEventTypeEvents, dateRange]);

  const eventNamesByDay = useMemo(
    () => toEventNamesByDay(allEventTypeEvents),
    [allEventTypeEvents],
  );

  const dataDayKeys = useMemo(
    () => toDayKeySet(allEventTypeEvents.filter((e) => eventIdsWithRollups.has(e.id))),
    [allEventTypeEvents, eventIdsWithRollups],
  );

  const pickerSlots = useMemo(
    () => ({
      day: (dayProps: DateRangePickerDayProps<DateTime>) => (
        <EventMarkerDay {...dayProps} dataDayKeys={dataDayKeys} eventNamesByDay={eventNamesByDay} />
      ),
      actionBar: () => (
        <Box
          sx={{
            gridRow: 3,
            gridColumn: '1 / 4',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-around',
            px: 2,
            pb: 1.5,
          }}
        >
          {[
            {
              color: 'success.main',
              Icon: FiberManualRecord,
              label: t('pages.playout_reports.date_range.legend.with_data'),
            },
            {
              color: 'error.main',
              Icon: FiberManualRecordOutlined,
              label: t('pages.playout_reports.date_range.legend.no_data'),
            },
          ].map(({ color, Icon, label }) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Icon sx={{ fontSize: 8, color }} />
              <Typography variant="caption" color="text.secondary">
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      ),
    }),
    [dataDayKeys, eventNamesByDay, t],
  );

  const deviceGroupOptions = useMemo(
    () =>
      (filterData?.network?.allDeviceGroups ?? []).map((dg) => ({
        id: dg.id,
        label: `${dg.name} (${dg.deviceCount} ${t('pages.playout_reports.counts.device_plural', {
          count: dg.deviceCount,
        })})`,
        disabled: dg.deviceCount === 0,
      })),
    [filterData, t],
  );

  const eventOptions = useMemo(() => {
    const now = DateTime.now();
    return eventsInRange
      .filter((e) => DateTime.fromISO(e.start) <= now)
      .sort((a, b) => b.start.localeCompare(a.start))
      .map((e) => {
        const hasData = eventIdsWithRollups.has(e.id);
        return {
          id: e.id,
          label: `${formatDateOnly(e.start, { showWeekday: false })} — ${e.name}${
            hasData ? '' : t('pages.playout_reports.events_status.no_data_suffix')
          }`,
          disabled: !hasData,
        };
      });
  }, [eventsInRange, eventIdsWithRollups, t]);

  const noEventsSelected = Array.isArray(selectedEventIds) && selectedEventIds.length === 0;
  const noGroupsSelected =
    Array.isArray(selectedDeviceGroupIds) && selectedDeviceGroupIds.length === 0;
  const querySkipped =
    eventTypeId === null ||
    dateRange[0] === null ||
    dateRange[1] === null ||
    noEventsSelected ||
    noGroupsSelected;

  const initializingEventType = eventTypeId !== null && dateRange[0] === null;

  const filterVariables = useMemo(
    () => ({
      networkId: currentNetwork.id,
      eventTypeId: eventTypeId ?? 0,
      from: dateRange[0]?.toISO() ?? null,
      to: dateRange[1]?.toISO() ?? null,
      deviceGroupIds: selectedDeviceGroupIds,
      eventIds: selectedEventIds,
    }),
    [currentNetwork.id, eventTypeId, dateRange, selectedDeviceGroupIds, selectedEventIds],
  );

  const queryVariables = useMemo(
    () => ({ ...filterVariables, combineByName }),
    [filterVariables, combineByName],
  );

  const { data: reportData, loading: reportLoading } = useQuery(PlayoutReportDocument, {
    skip: querySkipped,
    fetchPolicy: 'cache-and-network',
    variables: queryVariables,
  });

  const report = querySkipped ? EMPTY_REPORT : reportData?.network?.playoutReport ?? EMPTY_REPORT;
  const { stats } = report;

  const summaryLoading = initializingEventType || eventsLoading || (reportLoading && !reportData);

  // Hide the By Sponsor tab when the scope has no sponsor data, matching
  // how the Overview's sponsor charts and the top_assets sponsor column
  // hide themselves. The tab reappears if the user is already on a
  // by-sponsor route so they can still navigate back out — otherwise
  // they'd be stranded without a visible tab to click.
  const location = useLocation();
  const filterSearch = useMemo(
    () => serializePlayoutFilterParams(new URLSearchParams(), filters).toString(),
    [filters],
  );
  const visibleTabs = useMemo(() => {
    const hasSponsors = report.bySponsorPlays.length > 0;
    const onSponsorPath = /\/by-sponsor(\/|$)/.test(location.pathname);
    const entries =
      hasSponsors || onSponsorPath ? tabs : tabs.filter((tab) => tab.to !== 'by-sponsor');
    return entries.map((tab) => ({ ...tab, to: { pathname: tab.to, search: filterSearch } }));
  }, [report.bySponsorPlays.length, tabs, location.pathname, filterSearch]);

  const handleResetFilters = () => setFilters(DEFAULT_PLAYOUT_FILTERS);

  const [generate, { loading: exportLoading }] = useGenerateReport();
  const handleExport = async () => {
    if (eventTypeId === null || dateRange[0] === null || dateRange[1] === null) return;
    const { data } = await generate({
      variables: {
        input: {
          eventTypeId,
          from: dateRange[0].toISO() ?? '',
          to: dateRange[1].toISO() ?? '',
          deviceGroupIds: selectedDeviceGroupIds,
          eventIds: selectedEventIds,
        },
      },
    });
    if (data?.generateReport?.url) setDownloadUrl(data.generateReport.url);
  };

  const exportDisabled =
    eventTypeId === null || dateRange[0] === null || dateRange[1] === null || exportLoading;

  return (
    <>
      <Helmet title={t('pages.playout_reports.title')} />
      <LoadingPane in={filtersLoading && !filterData} size={80} thickness={4}>
        <Toolbar
          breadcrumbsLabel={<RouterBreadcrumbs />}
          titleIcon={<Assessment />}
          titleText={t('pages.playout_reports.title')}
          actions={
            <LoadingButton
              variant="contained"
              startIcon={<Download />}
              loading={exportLoading}
              loadingPosition="start"
              onClick={handleExport}
              disabled={exportDisabled}
              sx={{ textTransform: 'none' }}
            >
              {t('pages.playout_reports.buttons.export')}
            </LoadingButton>
          }
        />
        <PageContainer padding={false}>
          <Box sx={{ p: 2, height: '100%', overflowY: 'auto', scrollbarGutter: 'stable' }}>
            <Paper sx={{ p: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <Autocomplete
                  autoHighlight
                  options={eventTypes}
                  getOptionLabel={(o) => o.name}
                  getOptionDisabled={(o) => o.eventCount === 0}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  noOptionsText={t('pages.playout_reports.form.no_event_types')}
                  value={selectedEventType}
                  onChange={(_, v) => handleEventTypeChange(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('pages.playout_reports.form.placeholders.select_event_type')}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      {`${option.name} (${option.eventCount} ${t(
                        'pages.playout_reports.counts.event_plural',
                        { count: option.eventCount },
                      )})`}
                    </Box>
                  )}
                  sx={{ width: 'min(300px, 100%)' }}
                />
                <DateRangePicker
                  slots={pickerSlots}
                  localeText={{
                    start: t('pages.playout_reports.form.date_picker.from'),
                    end: t('pages.playout_reports.form.date_picker.to'),
                  }}
                  value={dateRange}
                  onChange={(newValue) => setFilters({ dateRange: newValue })}
                />
                <MultiSelectPicker
                  label={t('pages.playout_reports.filters.events_label')}
                  allLabel={t('pages.playout_reports.filters.all_events')}
                  itemLabelKey="common.pluralize.event_plural"
                  options={eventOptions}
                  selectedIds={selectedEventIds}
                  onChange={(ids) => setFilters({ eventIds: ids })}
                />
                <MultiSelectPicker
                  label={t('pages.playout_reports.form.placeholders.select_device_group')}
                  allLabel={t('pages.playout_reports.all_device_groups')}
                  itemLabelKey="common.pluralize.device_group_plural"
                  options={deviceGroupOptions}
                  selectedIds={selectedDeviceGroupIds}
                  onChange={(ids) => setFilters({ deviceGroupIds: ids })}
                />
                <Button
                  variant="outlined"
                  startIcon={<Clear />}
                  onClick={handleResetFilters}
                  disabled={
                    filters.eventTypeId === null &&
                    filters.dateRange[0] === null &&
                    filters.dateRange[1] === null &&
                    selectedEventIds === null &&
                    selectedDeviceGroupIds === null &&
                    combineByName
                  }
                  sx={{ height: 53, whiteSpace: 'nowrap', textTransform: 'none' }}
                >
                  {t('common.buttons.reset')}
                </Button>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 2,
                  mt: 2,
                }}
              >
                {eventTypeId !== null && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FiberManualRecord sx={{ fontSize: 10, color: 'success.main' }} />
                    <Typography variant="body1" color="text.secondary">
                      {dateRange[0] && dateRange[1]
                        ? `${formatDateOnly(dateRange[0], {
                            showWeekday: false,
                          })} - ${formatDateOnly(dateRange[1], { showWeekday: false })}`
                        : t('pages.playout_reports.date_range.full')}
                      {!summaryLoading &&
                        !querySkipped &&
                        t('pages.playout_reports.date_range.summary', {
                          events_count: stats.eventCount.toLocaleString(),
                          events: t('pages.playout_reports.counts.event_plural', {
                            count: stats.eventCount,
                          }),
                          assets_count: stats.uniqueAssets.toLocaleString(),
                          assets: t('pages.playout_reports.counts.asset_plural', {
                            count: stats.uniqueAssets,
                          }),
                          devices_count: stats.uniqueDevices.toLocaleString(),
                          devices: t('pages.playout_reports.counts.device_plural', {
                            count: stats.uniqueDevices,
                          }),
                          groups_count: stats.uniqueDeviceGroups.toLocaleString(),
                          groups: t('pages.playout_reports.counts.device_group_plural', {
                            count: stats.uniqueDeviceGroups,
                          }),
                        })}
                    </Typography>
                    {summaryLoading && (
                      <CircularProgress
                        aria-label={t('common.status.loading')}
                        size={14}
                        thickness={5}
                      />
                    )}
                  </Box>
                )}
                {currentUser.admin && (
                  // The help control sits beside the label, not inside it, so it is not a
                  // button within the switch's label.
                  <Box
                    sx={{ alignSelf: 'center', display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    <FormControlLabel
                      sx={{ whiteSpace: 'nowrap', mr: 0, ml: 0 }}
                      control={
                        <Switch
                          size="small"
                          checked={combineByName}
                          disabled={querySkipped}
                          onChange={(_, checked) => setFilters({ combineByName: checked })}
                        />
                      }
                      label={t('pages.playout_reports.filters.combine_duplicate_assets')}
                    />
                    <InfoTooltip
                      title={t('pages.playout_reports.filters.combine_duplicate_assets_tooltip')}
                    />
                  </Box>
                )}
              </Box>
            </Paper>

            {eventTypeId === null && (
              <EmptyState
                illustration={ReportingIllustration}
                header={t('pages.playout_reports.empty_state.select_event_type')}
              />
            )}

            {eventTypeId !== null && (
              <TabsLoadingPane
                in={initializingEventType || ((reportLoading || eventsLoading) && !reportData)}
                size={60}
                thickness={4}
                delay="0ms"
              >
                {!initializingEventType &&
                  (reportData || (querySkipped && !eventsLoading && !reportLoading)) && (
                    <>
                      <RouterTabs
                        tabs={visibleTabs}
                        variant="standard"
                        sx={{
                          flexShrink: 0,
                          backgroundColor: 'white',
                          border: (theme) => `1px solid ${theme.palette.borders.dark}`,
                          borderRadius: '5px 5px 0 0',
                          overflow: 'hidden',
                        }}
                      />
                      <Box
                        sx={{
                          px: 2,
                          pt: 2,
                          border: (theme) => `1px solid ${theme.palette.borders.dark}`,
                          borderTop: 'none',
                          borderRadius: '0 0 5px 5px',
                        }}
                      >
                        <PlayoutReportsProvider
                          report={report}
                          variables={queryVariables}
                          querySkipped={querySkipped}
                        >
                          <Outlet />
                        </PlayoutReportsProvider>
                      </Box>
                    </>
                  )}
              </TabsLoadingPane>
            )}
          </Box>
        </PageContainer>
      </LoadingPane>
      <ReportDownloadDialog
        close={() => setDownloadUrl(null)}
        from={dateRange[0]}
        reportUrl={downloadUrl}
        to={dateRange[1]}
      />
    </>
  );
};
